// src/components/reports/unifiedEventSummaryColumns.js
// Column definitions for the Unified Event Summary table.
// Separated from the page component to keep each file focused.

import React from "react";
import ErrorCellWithTooltip from "./ErrorCellWithTooltip";

/** Builds a Cell renderer for an error/kickout count column */
function makeErrorCell(valueKey, period, type) {
  return ({ row }) => (
    <ErrorCellWithTooltip
      source={row.original.Source}
      entity={row.original.Entity}
      value={row.original[valueKey]}
      period={period}
      type={type}
    />
  );
}

export const unifiedEventSummaryColumns = [
  {
    accessorKey: "Source",
    header: "Source",
    id: "Source",
    size: 170,
    minSize: 170,
    maxSize: 170,
    enableResizing: false,
  },
  {
    accessorKey: "Entity",
    header: "Entity",
    id: "Entity",
    size: 170,
  },
  {
    accessorKey: "EDSLastDateReceived",
    header: "Last Event Date (Time Zone = UTC)",
    id: "EDSLastDateReceived",
    size: 170,
  },
  {
    accessorKey: "EDSDaysNoDataReceived",
    header: "No Data Received Days",
    id: "EDSDaysNoDataReceived",
    size: 90,
  },
  {
    accessorKey: "EDSCurrentInqueueEvents",
    header: "Current Events in Queue",
    id: "EDSCurrentInqueueEvents",
    size: 130,
  },
  {
    accessorKey: "EDSQueueEventDay",
    header: "EDS Queue Event Day",
    id: "EDSQueueEventDay",
    size: 130,
  },
  {
    accessorKey: "EDSQueueName",
    header: "Processing Queue",
    id: "EDSQueueName",
    size: 130,
  },

  // EDS Events group
  {
    header: "EDS Events",
    columns: [
      { header: "T",   accessorKey: "EDSEventCountToday",      id: "EDSEventCountToday",      size: 60 },
      { header: "Y",   accessorKey: "EDSEventCountYesterday",  id: "EDSEventCountYesterday",  size: 60 },
      { header: "7D",  accessorKey: "EDSEventCountLast7Days",  id: "EDSEventCountLast7Days",  size: 65 },
      { header: "30D", accessorKey: "EDSEventCountLast30Days", id: "EDSEventCountLast30Days", size: 70 },
    ],
  },

  // Integration Errors group
  {
    header: "Integration Errors",
    columns: [
      { header: "T",   accessorKey: "IntegrationErrorCountToday",      id: "IntegrationErrorCountToday",      size: 60, Cell: makeErrorCell("IntegrationErrorCountToday",      "Today",     "integration") },
      { header: "Y",   accessorKey: "IntegrationErrorCountYesterday",  id: "IntegrationErrorCountYesterday",  size: 60, Cell: makeErrorCell("IntegrationErrorCountYesterday",  "Yesterday", "integration") },
      { header: "7D",  accessorKey: "IntegrationErrorCountLast7Days",  id: "IntegrationErrorCountLast7Days",  size: 65, Cell: makeErrorCell("IntegrationErrorCountLast7Days",  "7D",        "integration") },
      { header: "30D", accessorKey: "IntegrationErrorCountLast30Days", id: "IntegrationErrorCountLast30Days", size: 70, Cell: makeErrorCell("IntegrationErrorCountLast30Days", "30D",       "integration") },
    ],
  },

  // EDS Kickouts group
  {
    header: "EDS Kickouts",
    columns: [
      { header: "T",   accessorKey: "EDSKickoutCountToday",      id: "EDSKickoutCountToday",      size: 60, Cell: makeErrorCell("EDSKickoutCountToday",      "Today",     "kickout") },
      { header: "Y",   accessorKey: "EDSKickoutCountYesterday",  id: "EDSKickoutCountYesterday",  size: 60, Cell: makeErrorCell("EDSKickoutCountYesterday",  "Yesterday", "kickout") },
      { header: "7D",  accessorKey: "EDSKickoutCountLast7Days",  id: "EDSKickoutCountLast7Days",  size: 65, Cell: makeErrorCell("EDSKickoutCountLast7Days",  "7D",        "kickout") },
      { header: "30D", accessorKey: "EDSKickoutCountLast30Days", id: "EDSKickoutCountLast30Days", size: 70, Cell: makeErrorCell("EDSKickoutCountLast30Days", "30D",       "kickout") },
    ],
  },
];

// The three metric-group headers (EDS Events / Integration Errors / EDS
// Kickouts) each get their own light theme-tinted background so the groups
// are easy to tell apart, but the vertical DIVIDER lines between them are
// all the same accent color (theme primary/blue) — one column border on
// each side of the whole metric-group cluster, plus one after each group,
// reads as a clean consistent divider instead of a different color per
// junction.
const FIRST_GROUP_COLUMN = "EDSEventCountToday";

const GROUP_INDEX_BY_RIGHT_EDGE = {
  EDSEventCountLast30Days: 0,
  IntegrationErrorCountLast30Days: 1,
  EDSKickoutCountLast30Days: 2,
};

const GROUP_INDEX_BY_HEADER_NAME = {
  "EDS Events": 0,
  "Integration Errors": 1,
  "EDS Kickouts": 2,
};

export function getColumnBorderSx(columnId, theme) {
  const dividerBorder = `3px solid ${theme.palette.primary.main}`;
  const isGroupEnd = GROUP_INDEX_BY_RIGHT_EDGE[columnId] !== undefined;
  return {
    borderLeft: columnId === FIRST_GROUP_COLUMN ? dividerBorder : undefined,
    borderRight: isGroupEnd ? dividerBorder : undefined,
  };
}

export function getHeaderGroupBg(headerName, theme, getGroupAccent) {
  const idx = GROUP_INDEX_BY_HEADER_NAME[headerName];
  return idx !== undefined ? getGroupAccent(theme, idx).headerBg : undefined;
}

export const GROUP_HEADER_NAMES = new Set(Object.keys(GROUP_INDEX_BY_HEADER_NAME));
